import {
  FormControl,
  FormField,
  FormItem,
  useFormField,
} from '@/components/primitives/Form';
import { PhoneInput } from '@/components/primitives/PhoneInput';
import { cn } from '@/utils/cn';
import React from 'react';
import {
  useFormContext,
  type ControllerRenderProps,
  type FieldValues,
  type Path,
} from 'react-hook-form';

interface PhoneInputFieldContentProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends Path<TFieldValues> = Path<TFieldValues>,
> extends Omit<PhoneInputFieldProps<TFieldValues, TName>, 'name'> {
  field: ControllerRenderProps<TFieldValues, TName>;
}

const PhoneInputFieldContent = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends Path<TFieldValues> = Path<TFieldValues>,
>({
  field: { value, onChange, ...field },
  label,
  hideLabel,
  endAdornment,
  classNames,
  required,
  ...props
}: PhoneInputFieldContentProps<TFieldValues, TName>) => {
  const { error } = useFormField();

  return (
    <FormItem className={classNames?.wrapper}>
      <FormControl label={label} required={required} hideLabel={hideLabel}>
        <PhoneInput
          {...field}
          {...props}
          value={value}
          onChange={onChange}
          className={cn(classNames?.input, {
            ['border-error-text hover:border-error-text focus-visible:border-error-text focus-visible:ring-error-text']:
              error,
          })}
          aria-invalid={!!error}
          {...(endAdornment && { endAdornment })}
        />
      </FormControl>
    </FormItem>
  );
};

export interface PhoneInputFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends Path<TFieldValues> = Path<TFieldValues>,
> extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    'name' | 'className'
  > {
  label: React.ReactNode;
  name: TName;
  hideLabel?: boolean;
  endAdornment?: React.ReactNode;
  classNames?: { wrapper?: string; input?: string };
}

export const PhoneInputField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends Path<TFieldValues> = Path<TFieldValues>,
>({
  name,
  ...props
}: PhoneInputFieldProps<TFieldValues, TName>) => {
  const { control } = useFormContext<TFieldValues>();

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <PhoneInputFieldContent field={field} {...props} />
      )}
    />
  );
};

PhoneInputField.displayName = 'PhoneInputField';
